import { useState, useCallback } from 'react';
import { api } from '../api/client';

export interface FacilityRoom {
  id: number;
  facility_id: number;
  floor_index: number;
  floor_name: string;
  name: string;
  room_number: string | null;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /**
   * A door/window on a room wall.
   *   wall  – compass side ('n'|'s'|'e'|'w'). Anchor source of truth ONLY when `edge` is absent.
   *   pos   – 0..1 fraction. Along the bounding-box wall (no `edge`) OR along the matched
   *           polygon edge (`edge` present).
   *   type  – 'window' for a window; absent for a door.
   *   swing/hinge/width – door render props (see FacilityMap DoorsSvg).
   *
   *   edge  – OPTIONAL geometry signature, written ONLY when the door/window is placed or dragged
   *           on a NON-rectangular polygon room (L-shape, notch, Cut result, traced/diagonal
   *           import). It pins the opening to a specific polygon edge, which a bbox `wall`+`pos`
   *           can't express (that can only address the outer bounding extent, so a jogged wall's
   *           door snaps to the wrong line). Shape:
   *             fx, fy – that edge's midpoint as a fraction of the room's bounding box
   *                      (translation- & scale-invariant, so whole-room drags need no rewrite)
   *             nx, ny – the edge's OUTWARD unit normal, world coords
   *           Resolution (FacilityMap `resolveDoorAnchor`): match the signature to the nearest
   *           current polygon edge by normal alignment, then midpoint-fraction proximity, and
   *           anchor at `pos` along it. No raw edge index is stored, so it survives the room
   *           being reshaped or its vertices re-indexed by insert/delete/Cut/re-trace. Absent
   *           (every pre-existing door, and every door on a still-rectangular room) → fall back
   *           to the bbox `wall`+`pos` math, i.e. ZERO behaviour change for the common case.
   */
  doors: {
    wall: string;
    pos: number;
    edge?: { fx: number; fy: number; nx: number; ny: number };
  }[];
  fire_wall_edges: string[];
  linked_area_id: number | null;
  linked_location_id: number | null;
  needs_linking: boolean;
  dimensions_ft_w: number | null;
  dimensions_ft_h: number | null;
  notes: string | null;
  sort_order: number;
  points?: { x: number; y: number }[];
  color?: string | null;
  created_at: string;
  updated_at: string;
}

export type NewRoomData = Omit<FacilityRoom, 'id' | 'facility_id' | 'created_at' | 'updated_at'>;
export type RoomPatch = Partial<Omit<FacilityRoom, 'id' | 'facility_id' | 'created_at' | 'updated_at'>>;

// PostgreSQL NUMERIC columns arrive as strings via node-postgres — coerce to numbers
// so canvas math never operates on strings (which produces NaN / concatenation bugs).
const normalize = (r: any): FacilityRoom => ({
  ...r,
  x: Number(r.x),
  y: Number(r.y),
  w: Number(r.w),
  h: Number(r.h),
  dimensions_ft_w: r.dimensions_ft_w != null ? Number(r.dimensions_ft_w) : null,
  dimensions_ft_h: r.dimensions_ft_h != null ? Number(r.dimensions_ft_h) : null,
  floor_index: Number(r.floor_index),
  sort_order: Number(r.sort_order),
  linked_location_id: r.linked_location_id != null ? Number(r.linked_location_id) : null,
  doors: Array.isArray(r.doors) ? r.doors : [],
  fire_wall_edges: Array.isArray(r.fire_wall_edges) ? r.fire_wall_edges : [],
});

export default function useFacilityRooms() {
  const [rooms, setRooms] = useState<FacilityRoom[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (floorIndex?: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const params: Record<string, unknown> = {};
      if (floorIndex !== undefined) params.floor_index = floorIndex;
      const res = await api.get<FacilityRoom[]>('/facility-rooms', params);
      if (res.success) {
        setRooms((res.data ?? []).map(normalize));
      } else {
        setError('Failed to load rooms');
      }
    } catch {
      setError('Failed to load rooms');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createRoom = useCallback(async (data: NewRoomData): Promise<FacilityRoom | null> => {
    try {
      const res = await api.post<FacilityRoom>('/facility-rooms', data);
      if (res.success && res.data) {
        const room = normalize(res.data);
        setRooms(prev => [...prev, room]);
        return room;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const updateRoom = useCallback((id: number, changes: RoomPatch) => {
    setRooms(prev => prev.map(r => r.id === id ? { ...r, ...changes } : r));
  }, []);

  const deleteRoom = useCallback(async (id: number): Promise<boolean> => {
    try {
      const res = await api.delete(`/facility-rooms/${id}`);
      if (res.success) {
        setRooms(prev => prev.filter(r => r.id !== id));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const bulkUpdate = useCallback(async (dirtyRooms: FacilityRoom[]): Promise<boolean> => {
    try {
      const res = await api.put<FacilityRoom[]>('/facility-rooms/bulk', { rooms: dirtyRooms });
      if (res.success && res.data) {
        const updated = (res.data as FacilityRoom[]).map(normalize);
        setRooms(prev => {
          const map = new Map(updated.map(r => [r.id, r]));
          return prev.map(r => map.get(r.id) ?? r);
        });
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  return { rooms, setRooms, isLoading, error, refetch, createRoom, updateRoom, deleteRoom, bulkUpdate };
}
